import { BankAccountType } from "@/apis/gql/types";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { ComboBox } from "@/components/ui/combo-box";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TypographyH3 } from "@/components/ui/typography";
import getAccountTypeIcon from "@/lib/miscellaneous/get-account-type-icon";
import { formatIban } from "@/lib/utils";
import type { TranslationKey } from "@/localisation/types";
import { useCreateBankAccount } from "@/queries/bank-accounts/create";
import {
  type GetAllBankAccountsBankAccount,
  useGetAllBankAccounts,
} from "@/queries/bank-accounts/get-all";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

type BankAccountStepProps = {
  parsed: Array<Record<string, string>>;
  selectedBankAccountId: string;
  setSelectedBankAccountId: (bankAccountId: string) => void;
  onNext: (bankAccountId: string) => void;
  onBack: () => void;
  isCreating: boolean;
};

export default function BankAccountStep({
  parsed,
  selectedBankAccountId,
  setSelectedBankAccountId,
  onNext,
  onBack,
  isCreating,
}: BankAccountStepProps) {
  const { t } = useTranslation();
  const [showAlert, setShowAlert] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [createBankAccount, { loading: isCreatingBankAccount }] =
    useCreateBankAccount();

  const { bankAccounts } = useGetAllBankAccounts();

  if (!Array.isArray(parsed) || parsed.length === 0) {
    return null;
  }

  function handleNext() {
    if (!selectedBankAccountId) {
      setShowAlert(true);
      return;
    }
    setShowAlert(false);
    onNext(selectedBankAccountId);
  }

  return (
    <div className="mt-10">
      <TypographyH3 className="mb-2">
        {t("transactions.file_import.select_bank_account_title")}
      </TypographyH3>
      <p className="text-sm text-gray-600 mb-4 font-medium">
        {t("transactions.file_import.guidance_bank_account")}
      </p>
      <BankAccountSelector
        bankAccounts={bankAccounts}
        selectedBankAccountId={selectedBankAccountId}
        onChange={(id) => {
          setSelectedBankAccountId(id);
          setShowAlert(false);
        }}
      />
      <div className="text-sm text-gray-600 mt-4 ml-2">{t("or")}</div>
      <div className="mt-4">
        <Dialog open={openCreateDialog} onOpenChange={setOpenCreateDialog}>
          <DialogTrigger asChild>
            <Button variant="outline">
              {t("transactions.new_bank_account.button")}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {t("transactions.new_bank_account.title")}
              </DialogTitle>
              <DialogDescription>
                {t("transactions.new_bank_account.description")}
              </DialogDescription>
            </DialogHeader>

            <NewBankAccountForm
              onCancel={() => setOpenCreateDialog(false)}
              onCreate={async (values) => {
                const { bankAccountId } = await createBankAccount(values);
                setOpenCreateDialog(false);
                setSelectedBankAccountId(bankAccountId);
                onNext(bankAccountId);
              }}
            />
          </DialogContent>
        </Dialog>
      </div>

      <div className="fixed bottom-0 left-0 right-0 flex justify-center w-screen bg-white">
        <div className="max-w-4xl w-full w-full pl-6 pr-8 pb-4 gap-2 flex flex-col">
          {showAlert && (
            <Alert variant="destructive" className="mt-4 w-full">
              <AlertDescription>
                {t("transactions.file_import.select_bank_account_required")}
              </AlertDescription>
            </Alert>
          )}
          <div className="flex justify-between ">
            <Button variant="ghost" onClick={onBack}>
              {t("back")}
            </Button>
            <Button
              disabled={isCreating || isCreatingBankAccount}
              onClick={handleNext}
            >
              {t("next")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BankAccountSelector({
  bankAccounts,
  selectedBankAccountId,
  onChange,
}: {
  bankAccounts: GetAllBankAccountsBankAccount[];
  selectedBankAccountId: string;
  onChange: (bankAccountId: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="max-w-md">
      <ComboBox
        options={bankAccounts.map((bankAccount) => ({
          label: bankAccount.name,
          subLabel: bankAccount.iban ? formatIban(bankAccount.iban) : "",
          value: bankAccount.bankAccountId,
        }))}
        item={BankAccountItem}
        value={selectedBankAccountId || null}
        onChange={(v) => onChange(v || "")}
        placeholder={t(
          "transactions.file_import.select_bank_account_placeholder",
        )}
      />
    </div>
  );
}

type NewBankAccountFormValues = {
  name: string;
  type: BankAccountType;
};

function NewBankAccountForm({
  onCancel,
  onCreate,
}: {
  onCancel: () => void;
  onCreate: (values: NewBankAccountFormValues) => void;
}) {
  const { t } = useTranslation();
  const [typeOpen, setTypeOpen] = useState(false);

  const typeLabelKey: Record<BankAccountType, string> = {
    [BankAccountType.CHECKING]: "checking",
    [BankAccountType.SAVINGS]: "savings",
    [BankAccountType.CREDIT_CARD]: "creditCard",
    [BankAccountType.SECURITY]: "security",
    [BankAccountType.LOAN]: "loan",
    [BankAccountType.MEMBERSHIP]: "membership",
    [BankAccountType.BAUSPAREN]: "bausparen",
  } as const;

  const schema = z.object({
    name: z
      .string()
      .min(1, t("transactions.new_bank_account.validation.name_required")),
    type: z.nativeEnum(BankAccountType),
  });

  const form = useForm<NewBankAccountFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      type: BankAccountType.CHECKING,
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onCreate)} className="space-y-3">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("transactions.new_bank_account.form.name")}
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="type"
          render={({ field }) => {
            const AccountIcon = getAccountTypeIcon(field.value);
            return (
              <FormItem>
                <FormLabel>
                  {t("transactions.new_bank_account.form.type")}
                </FormLabel>
                <FormControl>
                  <Select
                    open={typeOpen}
                    onOpenChange={(o) => setTimeout(() => setTypeOpen(o), 0)}
                    value={field.value}
                    onValueChange={field.onChange}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={t(
                          "transactions.new_bank_account.form.type_placeholder",
                        )}
                      >
                        {field.value && (
                          <span className="flex items-center gap-2">
                            <AccountIcon className="h-4 w-4" />
                            {
                              t(
                                `bank_connections.account_type.${typeLabelKey[
                                  field.value
                                ].toLowerCase()}` as TranslationKey,
                              ) as string
                            }
                          </span>
                        )}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="z-[1001]">
                      {Object.values(BankAccountType).map((value) => {
                        const Icon = getAccountTypeIcon(value);
                        return (
                          <SelectItem key={value} value={value} hideIndicator>
                            <span className="flex items-center gap-2">
                              <Icon className="h-4 w-4" />
                              {
                                t(
                                  `bank_connections.account_type.${typeLabelKey[
                                    value as BankAccountType
                                  ].toLowerCase()}` as TranslationKey,
                                ) as string
                              }
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />

        <DialogFooter className="pt-2">
          <Button type="button" variant="ghost" onClick={onCancel}>
            {t("transactions.new_bank_account.form.cancel")}
          </Button>
          <Button type="submit">
            {t("transactions.new_bank_account.form.create")}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

function BankAccountItem({
  label,
  subLabel,
  value,
}: {
  label: string;
  subLabel?: string | undefined;
  value: string;
}) {
  return (
    <div className="flex flex-col">
      <div className="flex flex-row items-center gap-2">
        <div className="flex flex-col">
          <div className="text-sm font-medium">{label}</div>
          <div className="text-sm text-gray-500">{subLabel}</div>
        </div>
      </div>
    </div>
  );
}
